import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Star } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { BUSINESS, LOGO_URL } from "../lib/constants";

const Navbar = () => {
    const { lang, setLang, t } = useLanguage();
    const [open, setOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();

    const closeAll = () => {
        setOpen(false);
        setServicesOpen(false);
    };

    const navLinks = [
        { to: "/", label: t.nav.home },
        { to: "/aftercare", label: t.nav.aftercare },
        { to: "/bed-rentals", label: t.nav.bedRentals },
        { to: "/training", label: t.nav.training },
        { to: "/about", label: t.nav.about },
        { to: "/contact", label: t.nav.contact },
    ];

    const services = [
        { to: "/nano-brows", label: t.nav.nanoBrows },
        { to: "/combo-brows", label: t.nav.comboBrows },
        { to: "/powder-brows", label: t.nav.powderBrows },
        { to: "/lip-blushing", label: t.nav.lipBlushing },
        { to: "/microblading", label: t.nav.microblading },
        { to: "/eyeliner-tattoo", label: t.nav.eyelinerTattoo },
        { to: "/brow-lamination", label: t.nav.browLamination },
        { to: "/lashes", label: t.nav.lashes },
        { to: "/tattoos", label: t.nav.tattoos },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 bg-[#FFFFF3]/95 backdrop-blur-xl border-b border-[#3D3D24]/10"
        >
            {/* Top utility bar */}
            <div className="bg-[#3D3D24] text-[#FFFFF3] text-[11px] font-label py-2">
                <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between gap-6">
                    <div className="hidden md:flex items-center gap-4 opacity-90">
                        <a
                            data-testid="header-google-reviews"
                            href={BUSINESS.googleReviewsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 hover:text-[#D4A95A] transition-colors"
                            aria-label="Read our Google reviews"
                        >
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={11} fill="#D4A95A" stroke="#D4A95A" />
                            ))}
                            <span className="ml-1.5 tracking-[0.18em]">5.0 · 30+ Google Reviews</span>
                        </a>
                        <span className="opacity-50">·</span>
                        <span>Se Habla Español</span>
                        <span className="opacity-50">·</span>
                        <span>5 Min from LAS</span>
                    </div>
                    <div className="flex items-center gap-4 ml-auto">
                        <a
                            data-testid="header-phone-link"
                            href={`tel:${BUSINESS.phoneRaw}`}
                            className="hover:text-[#D4A95A] transition-colors"
                        >
                            {BUSINESS.phone}
                        </a>
                        <div className="flex items-center gap-1 border-l border-[#FFFFF3]/20 pl-4">
                            <button
                                data-testid="lang-toggle-en"
                                onClick={() => setLang("en")}
                                className={`px-1.5 transition-colors ${lang === "en" ? "text-[#D4A95A]" : "opacity-60 hover:opacity-100"}`}
                            >
                                EN
                            </button>
                            <span className="opacity-30">/</span>
                            <button
                                data-testid="lang-toggle-es"
                                onClick={() => setLang("es")}
                                className={`px-1.5 transition-colors ${lang === "es" ? "text-[#D4A95A]" : "opacity-60 hover:opacity-100"}`}
                            >
                                ES
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main nav */}
            <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-8">
                <Link
                    data-testid="nav-logo-link"
                    to="/"
                    onClick={closeAll}
                    className="flex items-center gap-3 mr-auto"
                >
                    <img
                        src={LOGO_URL}
                        alt="Beauty Ink LV"
                        className="h-16 md:h-20 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-7 text-sm">
                    <NavLink
                        data-testid="nav-link-home"
                        to="/"
                        end
                        className={({ isActive }) => `bink-nav-link text-[#3D3D24]/85 hover:text-[#3D3D24] ${isActive ? "active text-[#3D3D24]" : ""}`}
                    >
                        {t.nav.home}
                    </NavLink>

                    {/* Services dropdown — clickable to /services + hover dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setServicesOpen(true)}
                        onMouseLeave={() => setServicesOpen(false)}
                    >
                        <NavLink
                            data-testid="nav-services-trigger"
                            to="/services"
                            className={({ isActive }) => `bink-nav-link flex items-center gap-1.5 text-[#3D3D24]/85 hover:text-[#3D3D24] ${(isActive || services.some((s) => location.pathname === s.to)) ? "active text-[#3D3D24]" : ""}`}
                        >
                            {t.nav.services}
                            <ChevronDown size={14} />
                        </NavLink>
                        {servicesOpen && (
                            <div
                                data-testid="nav-services-dropdown"
                                className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                            >
                                <div className="bg-[#FFFFF3] border border-[#3D3D24]/15 min-w-[240px] shadow-xl">
                                    <NavLink
                                        data-testid="nav-service-all"
                                        to="/services"
                                        onClick={closeAll}
                                        className={({ isActive }) =>
                                            `block px-5 py-3 text-sm hover:bg-[#3D3D24] hover:text-[#FFFFF3] transition-colors border-b border-[#3D3D24]/10 font-label tracking-[0.18em] text-[11px] ${isActive ? "bg-[#3D3D24] text-[#FFFFF3]" : "text-[#D4A95A]"}`
                                        }
                                    >
                                        All Services →
                                    </NavLink>
                                    {services.map((s) => (
                                        <NavLink
                                            key={s.to}
                                            data-testid={`nav-service-${s.to.slice(1)}`}
                                            to={s.to}
                                            onClick={closeAll}
                                            className={({ isActive }) =>
                                                `block px-5 py-3 text-sm hover:bg-[#3D3D24] hover:text-[#FFFFF3] transition-colors border-b border-[#3D3D24]/10 last:border-0 ${isActive ? "bg-[#3D3D24] text-[#FFFFF3]" : "text-[#3D3D24]"}`
                                            }
                                        >
                                            {s.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {navLinks.slice(1).map((l) => (
                        <NavLink
                            key={l.to}
                            data-testid={`nav-link-${l.to.slice(1)}`}
                            to={l.to}
                            className={({ isActive }) => `bink-nav-link text-[#3D3D24]/85 hover:text-[#3D3D24] ${isActive ? "active text-[#3D3D24]" : ""}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <a
                    data-testid="nav-book-now-cta"
                    href="/contact"
                    className="hidden md:inline-flex bg-[#D4A95A] text-[#3D3D24] px-6 py-3 font-label text-xs hover:bg-[#b88f43] transition-colors"
                >
                    {t.common.bookNow}
                </a>

                <button
                    data-testid="mobile-menu-trigger"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-[#3D3D24]"
                    aria-label="Menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu-panel"
                    className="lg:hidden bg-[#FFFFF3] border-t border-[#3D3D24]/10 max-h-[80vh] overflow-y-auto"
                >
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {navLinks.slice(0, 1).map((l) => (
                            <NavLink
                                key={l.to}
                                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={closeAll}
                                className="py-3 border-b border-[#3D3D24]/10 text-[#3D3D24]"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="py-3 border-b border-[#3D3D24]/10">
                            <div className="font-label text-xs text-[#3D3D24]/60 mb-2">{t.nav.services}</div>
                            <NavLink
                                data-testid="mobile-service-all"
                                to="/services"
                                onClick={closeAll}
                                className="block py-2 pl-3 text-sm text-[#D4A95A] font-label tracking-[0.15em] text-[11px]"
                            >
                                — All Services
                            </NavLink>
                            {services.map((s) => (
                                <NavLink
                                    key={s.to}
                                    data-testid={`mobile-service-${s.to.slice(1)}`}
                                    to={s.to}
                                    onClick={closeAll}
                                    className="block py-2 pl-3 text-sm text-[#3D3D24]/85"
                                >
                                    — {s.label}
                                </NavLink>
                            ))}
                        </div>
                        {navLinks.slice(1).map((l) => (
                            <NavLink
                                key={l.to}
                                data-testid={`mobile-nav-${l.to.slice(1)}`}
                                to={l.to}
                                onClick={closeAll}
                                className="py-3 border-b border-[#3D3D24]/10 text-[#3D3D24]"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            data-testid="mobile-book-now-cta"
                            href="/contact"
                            onClick={closeAll}
                            className="mt-4 bg-[#D4A95A] text-[#3D3D24] px-6 py-4 text-center font-label text-xs"
                        >
                            {t.common.bookNow}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
